import { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import euroOfficeService, { euroOfficeEditorPath } from "api/euroOfficeService";
import officeService from "api/officeService";
import pluginService, { IOpenEditor } from "api/pluginService";
import settingsContext, { IOptionType } from "contexts/SettingsContext";
import { DocumentEditorKind, DocumentEditorPreference } from "dto/EuroOffice/IEuroOffice";
import usePlugin from "hooks/usePlugin";
import { ENotificationType, notification } from "utils/notification";

// Jeden punkt otwierania dokumentu do edycji. Edytor wybiera ustawienie użytkownika
// („editor.document.kind"), a gdy go nie ma — ustawienie urzędu (Ustawienia → Urząd).
// Euro-Office działa tylko, gdy BE ma skonfigurowany Document Server; w przeciwnym razie
// zostaje dotychczasowa ścieżka: wtyczka desktopowa + MS Word.

const DEFAULT_EDITOR: DocumentEditorKind = "word";

export const resolveDocumentEditor = (
  userPreference: DocumentEditorPreference | undefined,
  officeEditor: DocumentEditorKind | undefined,
  euroOfficeEnabled: boolean,
): DocumentEditorKind => {
  const wanted = userPreference || officeEditor || DEFAULT_EDITOR;
  return wanted === "euro-office" && !euroOfficeEnabled ? DEFAULT_EDITOR : wanted;
};

const useDocumentEditor = () => {
  const { t } = useTranslation();
  const { options } = useContext(settingsContext);
  const { data: office } = officeService.useGetOffice();
  const { data: status } = euroOfficeService.useGetStatus();
  const plugin = usePlugin();
  const { mutateAsync: openInPlugin } = pluginService.useOpenEditor();

  const editor = useMemo(() => {
    const preference = options.find((o) => o.Type === IOptionType.documentEditor)?.Value as
      | DocumentEditorPreference
      | undefined;
    return resolveDocumentEditor(preference, office?.data?.DocumentEditor, !!status?.Enabled);
  }, [options, office?.data?.DocumentEditor, status?.Enabled]);

  const openInEuroOffice = useCallback((payload: IOpenEditor) => {
    const opened = window.open(euroOfficeEditorPath(payload.Id, payload.Kind, payload.VersionId), "_blank");
    if (!opened) {
      notification({
        type: ENotificationType.ERROR,
        title: t("documentEditor.popupBlockedTitle"),
        text: t("documentEditor.popupBlockedText"),
      });
      return false;
    }
    return true;
  }, [t]);

  const openInWord = useCallback(
    async (payload: IOpenEditor) => {
      try {
        const version = await pluginService.getVersion();
        if (!version.IsConnected || plugin.isConnectionStale) {
          await plugin.connect();
        }
        await openInPlugin(payload);
        return true;
      } catch {
        notification({
          type: ENotificationType.ERROR,
          title: t("documentEditor.pluginMissingTitle"),
          text: t("documentEditor.pluginMissingText"),
        });
        return false;
      }
    },
    [plugin, openInPlugin, t],
  );

  /** Otwiera dokument w edytorze wynikającym z ustawień; zwraca `false`, gdy się nie udało (użytkownik dostał powiadomienie). */
  const openEditor = useCallback(
    (payload: IOpenEditor): Promise<boolean> =>
      editor === "euro-office" ? Promise.resolve(openInEuroOffice(payload)) : openInWord(payload),
    [editor, openInEuroOffice, openInWord],
  );

  return { editor, isEuroOffice: editor === "euro-office", openEditor };
};

export default useDocumentEditor;
